import { type AxiosError } from "axios";

import { logger } from "@/utils/logger";

import { axiosInstance } from "./axiosInstance";

/**
 * Two-Factor Authentication API Service
 *
 * Provides methods to interact with the 2FA backend endpoints
 */

// Type definitions
export interface TwoFactorStatus {
  enabled: boolean;
  hasBackupCodes: boolean;
  backupCodesCount: number;
}

export interface TwoFactorSetupResponse {
  success: boolean;
  secret: string;
  qrCode: string;
  otpauthUrl: string;
  message: string;
}

export interface VerifySetupResponse {
  success: boolean;
  backupCodes: string[];
  message: string;
}

export interface Verify2FALoginResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    email: string;
    name: string;
    authProvider: string;
    acceptPrivacy: boolean;
  };
}

export interface DisableResponse {
  success: boolean;
  message: string;
}

/**
 * Get 2FA status for the current authenticated user
 *
 * @returns Promise<TwoFactorStatus>
 */
export const get2FAStatus = async (): Promise<TwoFactorStatus> => {
  try {
    const response = await axiosInstance.get("api/v1/2fa/status");
    return response.data;
  } catch (error) {
    logger.error("Error fetching 2FA status", {
      error,
      context: "get2FAStatus",
      endpoint: "api/v1/2fa/status",
    });
    throw error;
  }
};

/**
 * Initialize 2FA setup - generates TOTP secret and QR code
 *
 * @returns Promise<TwoFactorSetupResponse>
 */
export const setup2FA = async (): Promise<TwoFactorSetupResponse> => {
  try {
    const response = await axiosInstance.post("/api/v1/2fa/setup");
    return response.data;
  } catch (error) {
    logger.error("Error setting up 2FA", {
      error,
      context: "setup2FA",
      endpoint: "/api/v1/2fa/setup",
    });
    throw error;
  }
};

/**
 * Verify TOTP code and complete 2FA setup
 *
 * @param token - 6-digit TOTP code from authenticator app
 * @returns Promise<VerifySetupResponse>
 */
export const verifySetup2FA = async (token: string): Promise<VerifySetupResponse> => {
  try {
    const response = await axiosInstance.post("/api/v1/2fa/verify-setup", { token });
    return response.data;
  } catch (error) {
    logger.error("Error verifying 2FA setup", {
      error,
      context: "verifySetup2FA",
      endpoint: "/api/v1/2fa/verify-setup",
    });
    throw error;
  }
};

/**
 * Verify 2FA code during login
 *
 * @param token - 6-digit TOTP code or 8-character backup code
 * @param isBackupCode - Whether the token is a backup code
 * @returns Promise<Verify2FALoginResponse>
 */
export const verify2FALogin = async (
  token: number,
  isBackupCode = false
): Promise<Verify2FALoginResponse> => {
  try {
    const response = await axiosInstance.post("/api/v1/2fa/verify-login", {
      token,
      isBackupCode,
    });
    return response.data;
  } catch (error) {
    logger.error("Error verifying 2FA login", {
      error,
      context: "verify2FALogin",
      endpoint: "/api/v1/2fa/verify-login",
    });
    throw error;
  }
};

/**
 * Disable 2FA for the current user
 *
 * @param password - User's password (required for LOCAL auth users)
 * @param token - 6-digit TOTP code or 8-character backup code
 * @param isBackupCode - Whether the token is a backup code
 * @returns Promise<DisableResponse>
 */
export const disable2FA = async (
  password: string | null,
  token: string,
  isBackupCode = false
): Promise<DisableResponse> => {
  try {
    const payload: any = {
      token,
      isBackupCode,
    };

    // Only include password if provided (for local auth)
    if (password) {
      payload.password = password;
    }

    const response = await axiosInstance.post("/api/v1/2fa/disable", payload);
    return response.data;
  } catch (error) {
    logger.error("Error disabling 2FA", {
      error,
      context: "disable2FA",
      endpoint: "/api/v1/2fa/disable",
    });
    throw error;
  }
};

/**
 * Handle Axios errors for 2FA operations
 *
 * @param error - Axios error object
 * @returns Error message string
 */
export const handle2FAError = (error: AxiosError): string => {
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data as any;

    switch (status) {
      case 400:
        return data?.error || "Invalid request. Please check your input.";
      case 401:
        return "Authentication required. Please login again.";
      case 429:
        return "Too many attempts. Please wait 1 minute before trying again.";
      case 500:
        return "Server error. Please try again later.";
      default:
        return data?.error || "An unexpected error occurred.";
    }
  } else if (error.request) {
    return "Network error. Please check your connection.";
  } else {
    return error.message || "An unexpected error occurred.";
  }
};
